/*@flow*/
import { useEffect } from 'react';
import removePathsFromQueryMap from 'lib/removePathsFromQueryMap';
import usePrev from 'lib/usePrev';
import useCommands from './useCommands';
import useContextState from './useContextState';
import type { LayerT } from './types';
import * as I from 'immutable';
import { q } from 'ops';

const dxFn = (s: { [key: string]: any }, v: ID) => ({
  ...s,
  [String(v)]: true,
});

export default function useLayerObserver() {
  const s = useContextState();
  const layersLoaded = s.layersLoaded;
  const layersActiveLayers = s.layersActiveLayers;
  const index = layersActiveLayers.reduce(dxFn, {});
  const cmd = useCommands();
  const cdbLayers: LayerT[] = layersLoaded.filter(
    (v) => index[v.uuid] && v.type === 'cdb'
  );
  const prevCdbLayers = usePrev(cdbLayers) || [];
  const removedCdbLayers = prevCdbLayers.filter((v) => !index[v.uuid]);

  function addLayerToDecks(layer) {
    const id = layer.uuid;
    if (!id) return;
    const name = `layer-${id}`;
    const layerMap = I.fromJS(layer);
    const deck = I.Map([[name, layerMap]]);
    cmd.update(['decks'], (v) => (v ? v.merge(deck) : deck));
  }

  function addLayerToQuery(layer) {
    const query = I.fromJS(layer).getIn(['query', 'deck']);
    if (!query) return;
    q((v) => v.update('deck', (v3) => (v3 ? v3.mergeDeep(query) : query)));
  }

  function clearLayerDecks() {
    cmd.update(['decks'], (v) => v.filterNot((_, k) => k.startsWith('layer-')));
  }

  function clearLayerQueries() {
    // We used usePrev to recall the last state of layersActiveLayers, and only
    // selected the removed layers.
    //
    // Remove layers from query string.
    removedCdbLayers.forEach((layer) => {
      const qy = layer.query;
      if (!qy) return;
      const queryMap = I.fromJS(qy);
      q((v) => removePathsFromQueryMap(v || I.Map(), queryMap));
    });
  }

  useEffect(() => {
    clearLayerDecks();
    clearLayerQueries();

    cdbLayers.forEach((layer) => {
      addLayerToDecks(layer);
      addLayerToQuery(layer);
    });
  }, [layersActiveLayers]);
}
